/**
 * useStageHistory Hook
 *
 * Fetches stage change history from activity_logs for a project.
 * Returns stage completion dates for timeline visualization.
 * Tracks full visit history for stages that are revisited.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";

export interface StageVisit {
  enteredAt: string;
  completedAt?: string;
}

export interface StageHistoryEntry {
  stageId: number;
  stageName: string;
  visits: StageVisit[];
  // Convenience getters for timeline display
  firstEnteredAt?: string;
  lastEnteredAt?: string;
  lastCompletedAt?: string;
  visitCount: number;
  isRevisited: boolean;
}

interface UseStageHistoryOptions {
  projectId: string | undefined;
  enabled?: boolean;
}

interface UseStageHistoryReturn {
  stageHistory: StageHistoryEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useStageHistory({
  projectId,
  enabled = true,
}: UseStageHistoryOptions): UseStageHistoryReturn {
  const [stageHistory, setStageHistory] = useState<StageHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStageHistory = useCallback(async () => {
    if (!projectId || !enabled) {
      setStageHistory([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch stage change events from activity_logs
      const { data, error: fetchError } = await supabase
        .from("activity_logs")
        .select("timestamp, details")
        .eq("project_id", projectId)
        .eq("action_type", "stage_changed")
        .order("timestamp", { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      if (!data || data.length === 0) {
        console.log('[useStageHistory] No data found for project:', projectId);
        setStageHistory([]);
        return;
      }

      console.log('[useStageHistory] Raw activity logs:', data);

      // Process the stage changes to build history with full visit tracking
      // Each log entry has: old_stage_id, new_stage_id, old_stage_name, new_stage_name
      // We process in chronological order to track every visit to each stage
      const historyMap = new Map<number, { stageId: number; stageName: string; visits: StageVisit[] }>();

      data.forEach((log) => {
        // Handle both formats:
        // Format 1 (newer): { old_stage: { id, name }, new_stage: { id, name } }
        // Format 2 (older): { old_stage_id, new_stage_id, old_stage_name, new_stage_name }
        const details = log.details as {
          // Format 1 - nested objects
          old_stage?: { id?: number; name?: string };
          new_stage?: { id?: number; name?: string };
          // Format 2 - flat fields
          old_stage_id?: number;
          new_stage_id?: number;
          old_stage_name?: string;
          new_stage_name?: string;
        };

        if (!details) return;

        // Extract values supporting both formats
        const oldStageId = details.old_stage?.id ?? details.old_stage_id;
        const oldStageName = details.old_stage?.name ?? details.old_stage_name;
        const newStageId = details.new_stage?.id ?? details.new_stage_id;
        const newStageName = details.new_stage?.name ?? details.new_stage_name;

        // When we leave a stage (old_stage), mark the current visit as completed
        if (oldStageId && oldStageName) {
          const existing = historyMap.get(oldStageId);
          if (existing && existing.visits.length > 0) {
            // Find the most recent visit that doesn't have a completedAt yet
            const openVisit = existing.visits.find((v) => !v.completedAt);
            if (openVisit) {
              openVisit.completedAt = log.timestamp;
            } else {
              // All visits are completed, this shouldn't happen in normal flow
              // but could happen with data inconsistencies - update the last one
              const lastVisit = existing.visits[existing.visits.length - 1];
              lastVisit.completedAt = log.timestamp;
            }
          } else {
            // First time seeing this stage (as old_stage), create entry with completed visit
            // This happens when project was already in this stage before tracking started
            historyMap.set(oldStageId, {
              stageId: oldStageId,
              stageName: oldStageName,
              visits: [{ enteredAt: "", completedAt: log.timestamp }],
            });
          }
        }

        // When we enter a stage (new_stage), start a new visit
        if (newStageId && newStageName) {
          const existing = historyMap.get(newStageId);
          if (existing) {
            // Check if the last visit is still open (no completedAt)
            const lastVisit = existing.visits[existing.visits.length - 1];
            if (lastVisit && !lastVisit.completedAt && !lastVisit.enteredAt) {
              // There's an open visit with no entry date (from old_stage processing)
              // Just update its entry date instead of creating a new visit
              lastVisit.enteredAt = log.timestamp;
            } else if (lastVisit && !lastVisit.completedAt) {
              // There's an open visit with an entry date - update the entry date
              // (this is a re-entry to the same stage before completing it)
              lastVisit.enteredAt = log.timestamp;
            } else {
              // Last visit is completed, add a new visit (this is a true re-entry)
              existing.visits.push({ enteredAt: log.timestamp });
            }
          } else {
            // First time entering this stage
            historyMap.set(newStageId, {
              stageId: newStageId,
              stageName: newStageName,
              visits: [{ enteredAt: log.timestamp }],
            });
          }
        }
      });

      // Convert to final format with convenience properties
      const history: StageHistoryEntry[] = Array.from(historyMap.values()).map((entry) => {
        const completedVisits = entry.visits.filter((v) => v.completedAt);
        const lastCompletedVisit = completedVisits.length > 0 ? completedVisits[completedVisits.length - 1] : undefined;
        const firstVisit = entry.visits[0];
        const lastVisit = entry.visits[entry.visits.length - 1];

        return {
          stageId: entry.stageId,
          stageName: entry.stageName,
          visits: entry.visits,
          firstEnteredAt: firstVisit?.enteredAt || undefined,
          lastEnteredAt: lastVisit?.enteredAt || undefined,
          lastCompletedAt: lastCompletedVisit?.completedAt,
          visitCount: entry.visits.length,
          isRevisited: entry.visits.length > 1,
        };
      });

      console.log('[useStageHistory] Processed history:', history);
      setStageHistory(history);
    } catch (err) {
      console.error("Error fetching stage history:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch stage history");
      setStageHistory([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchStageHistory();
  }, [fetchStageHistory]);

  return {
    stageHistory,
    loading,
    error,
    refetch: fetchStageHistory,
  };
}

export default useStageHistory;
